import CaseInfoHeader from "components/CaseInfoHeader";
import { Button, ButtonProps, Tooltip } from "@mui/material";
import ButtonSummary from "routes/lenses/lens/case/ButtonSummary";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { setAllIntakesReviewed, setDocumentsReviewed } from "redux/slices/case";
import { Case, CaseReviewStatus, Form } from "generated/graphql";
import { nonEmpty } from "helpers/coll";
import { useContext, useMemo, useState } from "react";
import { CaseFormContext } from "contexts/CaseFormContext";
import { checkMissingRequired } from "helpers/FormHelpers";
import { selectForm } from "redux/slices/form.slice";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import ReopenCaseDialog from "routes/lenses/lens/case/ReopenCaseDialog";
import { useShowSBR } from "SBR/useShowSBR";

interface CaseDetailGroupProps {
  myCase: Case;
}
export const CaseDetailGroup = ({ myCase }: CaseDetailGroupProps) => {
  const form: Form | undefined = useAppSelector(selectForm);
  const showSBR = useShowSBR();
  const cfContext = useContext(CaseFormContext);
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const missingRequiredProfileFields = useMemo(
    () => checkMissingRequired(form, cfContext.serverFieldValues),
    [form, cfContext.serverFieldValues]
  );
  const snackpatch = useAppDispatchWithSnackbar();
  const dispatch = useAppDispatch();

  const setCaseReviewStatus = (reviewStatus: CaseReviewStatus, reviewStatusReason?: string) => {
    const successMessage = reviewStatus === "new" ? "Case reopened" : "Case completed";
    const errorMessage = reviewStatus === "new" ? "Unable to reopen case" : "Unable to complete case";
    snackpatch(
      reviewStatus === "new"
        ? setDocumentsReviewed({ caseId: myCase.id, reviewed: false, reviewStatusReason })
        : setDocumentsReviewed({ caseId: myCase.id, reviewed: true }),
      {
        successMessage,
        errorMessage,
      }
    );
  };
  const actions = (reviewStatus?: CaseReviewStatus) => {
    let text = "Reopen Review";
    let variant: ButtonProps["variant"] = "text";
    let disabled = false;
    let disabledTitle = "";
    let onClick = () => setShowReopenDialog(true);
    if (reviewStatus !== "complete") {
      text = "Complete Review";
      variant = "contained";
      onClick = () => {
        setCaseReviewStatus(CaseReviewStatus.Complete);
        dispatch(setAllIntakesReviewed());
      };

      if (nonEmpty(missingRequiredProfileFields)) {
        disabled = true;
        disabledTitle = `Please complete required fields ${missingRequiredProfileFields.map((f) => f).join(", ")}`;
      }
    }
    const button = (
      <Button color="primary" variant={variant} onClick={onClick} disabled={disabled}>
        {text}
      </Button>
    );

    const completeReviewButton = disabled ? (
      <Tooltip arrow title={disabledTitle}>
        <span>{button}</span>
      </Tooltip>
    ) : (
      button
    );

    return completeReviewButton;
  };

  return (
    <div className="flex items-center">
      <CaseInfoHeader mycase={myCase} />
      <ButtonSummary />
      {!showSBR && actions(myCase.reviewStatus)}

      <ReopenCaseDialog
        onCancel={() => setShowReopenDialog(false)}
        onReopen={(reviewStatusReason) => {
          setShowReopenDialog(false);
          setCaseReviewStatus(CaseReviewStatus.New, reviewStatusReason);
        }}
        showDialog={showReopenDialog}
      />
    </div>
  );
};
